import { useTranslation } from "react-i18next";
import React, {
  createContext,
  memo,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { AlertCircle, RotateCw, Sparkles } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { BubbleAttachments } from "@/components/ai-assistant/attachment-chips";
import {
  MarkdownImage,
  MarkdownLink,
} from "@/components/ai-assistant/markdown-media";
import { PdfDownloadable } from "@/components/ai-assistant/pdf-downloadable";
import {
  PdfExportContext,
  usePdfExportValue,
  usePdfPartIndex,
} from "@/components/ai-assistant/pdf-export-context";
import { GeneratedAttachments } from "@/components/ai-assistant/generated-attachments";
import { Button } from "@/components/ui/button";
import { loadMermaid } from "@/utils/load-mermaid";
import { detectFencedForm } from "@/components/ai-assistant/maya-form";

import { PALETTE } from "../constants";
import type { IMayaMessage } from "../types";

import { PromptActions, PromptEditor, ReplyActions } from "./message-actions";
import { TypingIndicator } from "./typing-indicator";

const sweepMermaidErrorSvgs = () => {
  document
    .querySelectorAll('body > svg[aria-roledescription="error"]')
    .forEach((el) => el.remove());
};

const MermaidChart = ({
  chart,
  onRender,
}: {
  chart: string;
  onRender?: () => void;
}) => {
  const { t } = useTranslation(["common"]);
  const idRef = useRef(`mermaid-${Math.random().toString(36).slice(2)}`);
  const [svg, setSvg] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  // Claim this chart's position once on mount (its own "Save PDF").
  const chartIndex = usePdfPartIndex("chart");

  useEffect(() => {
    let cancelled = false;
    loadMermaid()
      .then((mermaid) => mermaid.render(idRef.current, chart))
      .then(({ svg: rendered }) => {
        if (cancelled) return;
        setSvg(rendered);
        setHasError(false);
        onRender?.();
      })
      .catch(() => {
        if (!cancelled) setHasError(true);
        sweepMermaidErrorSvgs();
      });
    return () => {
      cancelled = true;
      document.getElementById("d" + idRef.current)?.remove();
      document.getElementById(idRef.current)?.remove();
    };
  }, [chart]);

  if (hasError)
    return (
      <pre className="text-[11px] text-danger whitespace-pre-wrap">{chart}</pre>
    );
  if (!svg)
    return (
      <div className="text-[11px] text-muted-foreground py-1 animate-pulse">
        {t("my-space:ui.rendering_chart")}
      </div>
    );
  return (
    <PdfDownloadable part="chart" index={chartIndex} className="my-1">
      <div
        dangerouslySetInnerHTML={{ __html: svg }}
        className="overflow-x-auto py-1"
      />
    </PdfDownloadable>
  );
};

interface MessageBubbleProps {
  message: IMayaMessage;
  onRetry?: () => void;
  onChartRender?: () => void;
  /** A reply is streaming or pending — actions that start one must wait. */
  busy?: boolean;
  /** This reply is being regenerated / re-answered after a prompt edit. */
  pending?: boolean;
  /** Picked from the chat list — pulsed once to draw the eye. */
  highlighted?: boolean;
  onEdit?: (id: string, text: string) => Promise<boolean>;
  onRegenerate?: (id: string) => void;
}

const ChartRenderContext = createContext<(() => void) | undefined>(undefined);

const MERMAID_DIAGRAM_RE =
  /^(pie|graph|flowchart|sequenceDiagram|classDiagram|stateDiagram(?:-v2)?|erDiagram|gantt|journey|gitGraph|mindmap|timeline|quadrantChart|sankey-beta|xychart-beta|requirementDiagram|C4Context|C4Container|C4Component|C4Dynamic|C4Deployment)\b/;

const isMermaidContent = (lang: string, content: string) =>
  lang === "mermaid" || MERMAID_DIAGRAM_RE.test(content);

const CodeRenderer = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLElement>) => {
  const onChartRender = useContext(ChartRenderContext);
  const isBlock = Boolean(className?.startsWith("language-"));
  const lang = className?.replace("language-", "") ?? "";
  const text = String(children).trim();
  if (isBlock && isMermaidContent(lang, text)) {
    return <MermaidChart chart={text} onRender={onChartRender} />;
  }
  if (isBlock)
    return (
      <code className="font-mono text-[11px]" {...props}>
        {children}
      </code>
    );
  return (
    <code
      className="bg-muted rounded px-1 py-0.5 text-[11px] font-mono text-foreground"
      {...props}
    >
      {children}
    </code>
  );
};

/** A table with its own "Save PDF"; claims its position once on mount. */
const TableBlock = (props: React.ComponentProps<"table">) => {
  const tableIndex = usePdfPartIndex("table");
  return (
    <PdfDownloadable part="table" index={tableIndex} className="my-1.5">
      <div className="overflow-x-auto">
        <table className="border-collapse text-[11px] w-full" {...props} />
      </div>
    </PdfDownloadable>
  );
};

const MARKDOWN_COMPONENTS = {
  table: (props: React.ComponentProps<"table">) => <TableBlock {...props} />,
  thead: ({ ...props }) => <thead className="bg-muted" {...props} />,
  th: ({ ...props }) => (
    <th
      className="border border-border px-2 py-1 text-start font-semibold text-foreground"
      {...props}
    />
  ),
  td: ({ ...props }) => (
    <td
      className="border border-border px-2 py-1 text-muted-foreground"
      {...props}
    />
  ),
  pre: ({ ...props }) => (
    <pre
      className="bg-muted rounded-md p-2 text-[11px] overflow-x-auto my-1.5 font-mono"
      {...props}
    />
  ),
  code: CodeRenderer,
  p: ({ ...props }) => (
    <p className="mb-1 last:mb-0 text-foreground" {...props} />
  ),
  h1: ({ ...props }) => (
    <h1 className="text-sm font-bold mb-1 mt-1.5 text-foreground" {...props} />
  ),
  h2: ({ ...props }) => (
    <h2
      className="text-[13px] font-bold mb-1 mt-1.5 text-foreground"
      {...props}
    />
  ),
  h3: ({ ...props }) => (
    <h3
      className="text-[12px] font-semibold mb-0.5 text-foreground"
      {...props}
    />
  ),
  ul: ({ ...props }) => (
    <ul
      className="list-disc list-inside mb-1 space-y-0.5 text-foreground"
      {...props}
    />
  ),
  ol: ({ ...props }) => (
    <ol
      className="list-decimal list-inside mb-1 space-y-0.5 text-foreground"
      {...props}
    />
  ),
  li: ({ ...props }) => <li className="text-[12px]" {...props} />,
  blockquote: ({ ...props }) => (
    <blockquote
      className="border-s-2 border-border-strong ps-2 my-1 text-muted-foreground italic"
      {...props}
    />
  ),
  // Same link/image handling as the full page: image links and images open
  // in the in-app preview; other links open in a new tab.
  a: MarkdownLink,
  img: MarkdownImage,
  hr: () => <hr className="my-2 border-border" />,
  strong: ({ ...props }) => (
    <strong className="font-semibold text-foreground" {...props} />
  ),
  em: ({ ...props }) => (
    <em className="italic text-muted-foreground" {...props} />
  ),
} as const;

const REMARK_PLUGINS = [remarkGfm];

const BotMarkdown = memo(
  ({
    content,
    messageId,
    onChartRender,
  }: {
    content: string;
    /** Stored id of the reply — tables/charts export to PDF by it. */
    messageId: number | null;
    onChartRender?: () => void;
  }) => {
    const pdfExport = usePdfExportValue(messageId, content, {
      hideUnsaved: true,
    });
    return (
      <ChartRenderContext.Provider value={onChartRender}>
        <PdfExportContext.Provider value={pdfExport}>
          {/* Keyed by content: a change remounts the subtree, so every
              table/chart re-claims its PDF index from the reset counters. */}
          <ReactMarkdown
            key={content}
            remarkPlugins={REMARK_PLUGINS}
            components={MARKDOWN_COMPONENTS}
          >
            {content}
          </ReactMarkdown>
        </PdfExportContext.Provider>
      </ChartRenderContext.Provider>
    );
  },
);
BotMarkdown.displayName = "BotMarkdown";

const CHART_FENCE_RE =
  /```(?:mermaid|pie|graph|flowchart|sequenceDiagram|classDiagram|stateDiagram(?:-v2)?|erDiagram|gantt|journey|gitGraph|mindmap|timeline|quadrantChart|sankey-beta|xychart-beta|requirementDiagram|C4(?:Context|Container|Component|Dynamic|Deployment))\b/;

const hasChartContent = (text: string) => CHART_FENCE_RE.test(text);

const MessageBubbleView = ({
  message,
  onRetry,
  onChartRender,
  busy = false,
  pending = false,
  highlighted = false,
  onEdit,
  onRegenerate,
}: MessageBubbleProps) => {
  const { t } = useTranslation(["common"]);
  // The ```maya-form fence becomes the form above the composer — not raw JSON.
  const botText = message.i18nKey
    ? t(message.text)
    : message.role === "user"
      ? message.text
      : detectFencedForm(message.text).content;
  const isUser = message.role === "user";
  const failed = message.status === "failed";
  const isWide = !isUser && hasChartContent(message.text);
  // The greeting is copy, not a turn — no actions on it.
  const actionable = !message.i18nKey;

  // Editing a prompt happens in place; closing the editor hands focus back
  // to the edit button rather than dropping it.
  const [editing, setEditing] = useState(false);
  // What the editor opens with: the prompt, or the text of an edit that
  // failed, so it can be tried again.
  const [editDraft, setEditDraft] = useState("");
  const rootRef = useRef<HTMLDivElement | null>(null);
  const editButtonRef = useRef<HTMLButtonElement | null>(null);
  const wasEditingRef = useRef(false);
  useEffect(() => {
    if (wasEditingRef.current && !editing) {
      // After a save the button is disabled while the new answer is on its
      // way, so focus goes to the message itself instead of being dropped.
      const button = editButtonRef.current;
      if (button && !button.disabled) button.focus();
      else rootRef.current?.focus();
    }
    wasEditingRef.current = editing;
  }, [editing]);
  const startEdit = () => {
    setEditDraft(message.text);
    setEditing(true);
  };
  const saveEdit = (text: string) => {
    // The new text shows at once and the reply below goes to "thinking";
    // a failure puts the old text back and reopens the editor with the edit.
    setEditing(false);
    void onEdit?.(message.id, text).then((saved) => {
      if (saved) return;
      setEditDraft(text);
      setEditing(true);
    });
  };

  // The user's turns are compact gradient pills on the inline-end side; Maya's
  // replies are not bubbles at all — text on the canvas beside the orb, so a
  // long answer (tables, charts) reads like a page rather than a speech balloon.
  return (
    <div
      ref={rootRef}
      // Focusable from script only: where focus lands after an edit.
      tabIndex={-1}
      data-message-id={message.id}
      className={`animate-rise-in flex flex-col gap-1 rounded-xl outline-hidden ${isUser ? "items-end" : "items-start"} ${highlighted ? "maya-highlight" : ""}`}
    >
      {isUser ? (
        <>
          {/* Files the user sent sit above their message, as on the full
              page; a file-only turn has no text pill at all. */}
          {message.attachments?.length ? (
            <BubbleAttachments
              attachments={message.attachments}
              variant="assistant"
              size="sm"
              align="end"
              className="max-w-[85%]"
            />
          ) : null}
          {editing ? (
            <PromptEditor
              initial={editDraft}
              original={message.text}
              busy={busy}
              onSave={saveEdit}
              onCancel={() => setEditing(false)}
            />
          ) : (
            message.text && (
              <div
                className={`max-w-[85%] whitespace-pre-wrap wrap-break-word rounded-2xl rounded-ee-md px-3.5 py-2 text-[12px] leading-snug text-primary-foreground shadow-sm ${
                  failed ? "opacity-60" : ""
                }`}
                style={{ background: PALETTE.userBubble }}
              >
                {message.text}
              </div>
            )
          )}
          {actionable && !editing && (
            <PromptActions
              message={message}
              busy={busy}
              onEdit={onEdit ? startEdit : undefined}
              editButtonRef={editButtonRef}
            />
          )}
        </>
      ) : pending ? (
        // Regenerating, or re-answering an edited prompt: the old answer is
        // kept underneath and comes back if this is stopped or fails.
        <TypingIndicator />
      ) : (
        <div className="flex w-full items-start gap-2.5">
          {/* A light static mark, not the live orb: one per reply adds up. */}
          <span
            aria-hidden="true"
            className="bg-gradient-brand mt-0.5 grid size-6 shrink-0 place-items-center rounded-full text-primary-foreground shadow-sm"
          >
            <Sparkles className="size-3" />
          </span>
          <div
            className={`min-w-0 flex-1 wrap-break-word pt-0.5 text-[12px] leading-relaxed text-foreground ${
              isWide ? "" : "max-w-[95%]"
            }`}
          >
            {botText && (
              <BotMarkdown
                content={botText}
                messageId={
                  typeof message.serverId === "number" ? message.serverId : null
                }
                onChartRender={onChartRender}
              />
            )}
            {/* Files the AI produced for this reply (a CSV export, a PDF) —
                download cards, plus any that couldn't be prepared. */}
            <GeneratedAttachments
              attachments={message.generatedAttachments}
              errors={message.attachmentErrors}
              className={botText ? "mt-2" : ""}
            />
            {actionable && onRegenerate && (
              <div className="mt-1">
                <ReplyActions
                  message={message}
                  busy={busy}
                  onRegenerate={onRegenerate}
                />
              </div>
            )}
          </div>
        </div>
      )}
      {failed && onRetry && (
        <Button
          type="button"
          variant="link"
          size="xs"
          onClick={onRetry}
          className="gap-1 text-[10px] text-danger"
        >
          <AlertCircle className="size-3" />
          {t("common:chrome.message_failed")}
          <span className="inline-flex items-center gap-0.5 underline">
            <RotateCw className="size-2.5" />
            {t("common:buttons.retry")}
          </span>
        </Button>
      )}
    </div>
  );
};

/** Memoised: a message is immutable once rendered, and re-parsing every
 *  reply's markdown on each chat update (status lines, route changes) adds up. */
export const MessageBubble = memo(MessageBubbleView);
